/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2025 ICEMEDX, Inc.
***********************************************/
import { Injectable } from "@angular/core";
import { IceEntity } from "../../models/core/IceEntity";
import { notebookpage } from "../../models/customforms/notebookpage";
import { IceTypes } from "../../models/icetypes";
import { BaseAppService } from "../../services/baseapp.service";
import { CustomFormService } from "../../services/custom/customform.service";
import { Iceutility } from "../../services/iceutility";
import { MedicalcodesService } from "../../services/medicalcodes/medicalcodes.service";
import { IceRect } from "../icemultimedia/IceMultimediaStroke";
import { IcecontrolSettings } from "./icesettings";
import { basecontrol, ctrl_type, fld_type, NotebookItemObject, NotebookTable, notebook_constants, ss_ctrl_type } from "./notebookitemobject";
import { NotebookPageObject } from "./notebookpageobject";

@Injectable()
export class NotebookObject extends IceEntity {
  currentPage: number = -1;
  m_pCurrentParentWnd: HTMLDivElement = null;
  m_nreason_for_visit: number = 0;
  m_strCField: string = "";
  m_pages: NotebookPageObject[] = [];
  m_data_record: any = {};
  m_strPatientName: string = "";
  m_strPatientSex: string = "";
  m_strCurrentDate: string = "";
  m_strDOB: string = "";
  m_strOtherMedications: string = "";
  m_strMedicalProblems: string = "";
  m_arAllergies: string[] = [];
  m_strDoctorSignature: string = "";
  m_strProcedureNote: string = "";;
  m_strExamDate: string = "";

  m_bOtherMedsModified: boolean = false;
  m_bMedicalProblemsModified: boolean = false;
  m_strPatientIns: string = "";

  m_nSrcType: IceTypes.SourceType = IceTypes.SourceType.srcUndefined;

  constructor(private formSvc: CustomFormService, private appSvc: BaseAppService, private codeSvc: MedicalcodesService) {
    super();
  }

  getFormService(): CustomFormService {
    return this.formSvc;
  }

  getCurrentPage(): number {
    return this.currentPage;
  }

  getPatientName(): string {
    return this.m_strPatientName;
  }

  getPatientIns(): string {
    return this.m_strPatientIns;
  }

  getPatientSex(): string {
    return this.m_strPatientSex;
  }

  getCurrentDate(): string {
    return this.m_strCurrentDate;
  }

  getDOB(): string {
    return this.m_strDOB;
  }

  setPatientName(lpszPatientName: string): void {
    this.m_strPatientName = lpszPatientName;
  }

  setPatientIns(lpszPatientIns: string) {
    this.m_strPatientIns = lpszPatientIns;
  }

  setPatientSex(lpszPatientSex: string): void {
    this.m_strPatientSex = lpszPatientSex;
  }

  setCurrentDate(lspzDate: string): void {
    this.m_strCurrentDate = lspzDate;
  }

  setDOB(lpszDate: string): void {
    this.m_strDOB = lpszDate;
  }

  setExamDate(lpszDate: string): void {
    this.m_strExamDate = lpszDate;
  }

  getDoctorSignature(): string {
    return this.m_strDoctorSignature;
  }

  setDoctorSignature(lspzDocSignature: string): void {
    this.m_strDoctorSignature = lspzDocSignature;
  }

  getProcedureNote(): string {
    return this.m_strProcedureNote;
  }

  setProcedureNote(lspzProcedureNote: string): void {
    this.m_strProcedureNote = lspzProcedureNote;
  }

  getOtherMedications(): string {
    return this.m_strOtherMedications;
  }

  getMedicalProblems(): string {
    return this.m_strMedicalProblems;
  }

  getExamDate(): string {
    return this.m_strExamDate;
  }

  getAllergies(): string[] {
    return this.m_arAllergies;
  }

  setOtherMedications(lpszMeds: string): void {
    this.m_strOtherMedications = lpszMeds;
  }

  setMedicalProblems(lpszMedicalProblems: string): void {
    this.m_strMedicalProblems = lpszMedicalProblems;
  }

  setAllergies(rarAllergies: string[]): void {
    if (rarAllergies == null)
      return;

    try {
      this.m_arAllergies.concat(rarAllergies);
    }
    catch (error)
    {
      console.log(error);
    }
  }

  setDefaultSourceType(nSrcType: IceTypes.SourceType): void {
    this.m_nSrcType = nSrcType;
  }

  getDefaultSourceType(): IceTypes.SourceType {
    return this.m_nSrcType;
  }

  static copy(pNBPItem: NotebookItemObject, pitem: IceEntity) {
    if (pNBPItem == null || pitem == null) return false;

    let strValue = pitem.getStringValue(pitem.attributes, notebook_constants.szVALUE_TOKEN);
    if (strValue.length > 0) {
      // To Do
    }

    let strCaption = pitem.getStringValue(pitem.attributes, notebook_constants.szCAPTION_TOKEN);
    if (strCaption.length > 0) {
      pNBPItem.setCaption(strCaption);
    }

    let strX1 = pitem.getStringValue(pitem.attributes, notebook_constants.szX1_TOKEN);
    if (strX1.length > 0) {
      pNBPItem.setX1(Iceutility.toLong(strX1));
    }

    let strY1 = pitem.getStringValue(pitem.attributes, notebook_constants.szY1_TOKEN);
    if (strY1.length > 0) {
      pNBPItem.setY1(Iceutility.toLong(strY1));
    }

    let strX2 = pitem.getStringValue(pitem.attributes, notebook_constants.szX2_TOKEN);
    if (strX2.length > 0) {
      pNBPItem.setX2(Iceutility.toLong(strX2));
    }

    let strY2 = pitem.getStringValue(pitem.attributes, notebook_constants.szY2_TOKEN);
    if (strY2.length > 0) {
      pNBPItem.setY2(Iceutility.toLong(strY2));
    }

    let strWidth = pitem.getStringValue(pitem.attributes, notebook_constants.szWIDTH_TOKEN);
    if (strWidth.length > 0) {
      pNBPItem.setWidth(Iceutility.toLong(strWidth));
    }

    let strHeight = pitem.getStringValue(pitem.attributes, notebook_constants.szHEIGHT_TOKEN);
    if (strHeight.length > 0) {
      pNBPItem.setHeight(Iceutility.toLong(strHeight));
    }

    let strSSX1 = pitem.getStringValue(pitem.attributes, notebook_constants.szSSX1_TOKEN);
    if (strSSX1.length > 0) {
      pNBPItem.setSSX1(Iceutility.toLong(strSSX1));
    }

    let strSSY1 = pitem.getStringValue(pitem.attributes, notebook_constants.szSSY1_TOKEN);
    if (strSSY1.length > 0) {
      pNBPItem.setSSY1(Iceutility.toLong(strSSY1));
    }

    let strSSX2 = pitem.getStringValue(pitem.attributes, notebook_constants.szSSX2_TOKEN);
    if (strSSX2.length > 0) {
      pNBPItem.setSSX2(Iceutility.toLong(strSSX2));
    }

    let strSSY2 = pitem.getStringValue(pitem.attributes, notebook_constants.szSSY2_TOKEN);
    if (strSSY2.length > 0) {
      pNBPItem.setSSY2(Iceutility.toLong(strSSY2));
    }

    let strFieldType = pitem.getStringValue(pitem.attributes, notebook_constants.szFIELDTYPE_TOKEN);
    if (strFieldType.length > 0) {
      strFieldType = strFieldType.toUpperCase();
      if (strFieldType == notebook_constants.szFIELD_TOKEN)
        pNBPItem.setFieldType(fld_type.ftypField);
      else if (strFieldType == notebook_constants.szCUSTOMFIELD_TOKEN)
        pNBPItem.setFieldType(fld_type.ftypCustom);
      else if (strFieldType == notebook_constants.szOPTIONFIELD_TOKEN)
        pNBPItem.setFieldType(fld_type.ftypOption);
    }

    let strTableName = pitem.getStringValue(pitem.attributes, notebook_constants.szTABLENAME_TOKEN);
    if (strTableName.length > 0) {
      pNBPItem.setTableName(strTableName);
    }

    let strFieldName = pitem.getStringValue(pitem.attributes, notebook_constants.szFIELDNAME_TOKEN);
    if (strFieldName.length > 0) {
      pNBPItem.setFieldName(strFieldName);
    }

    let strFontName = pitem.getStringValue(pitem.attributes, notebook_constants.szFONTNAME_TOKEN);
    if (strFontName.length > 0) {
      pNBPItem.setFontName(strFontName);
    }

    let strFontColor = pitem.getStringValue(pitem.attributes, notebook_constants.szFONTCOLOR_TOKEN);
    if (strFontColor.length > 0) {
      pNBPItem.setFontColor(strFontColor);
    }

    let strFontSize = pitem.getStringValue(pitem.attributes, notebook_constants.szFONTSIZE_TOKEN);
    if (strFontSize.length > 0) {
      pNBPItem.setFontSize(Iceutility.toLong(strFontSize));
    }

    let strFontWeight = pitem.getStringValue(pitem.attributes, notebook_constants.szFONTWEIGHT_TOKEN);
    if (strFontWeight.length > 0) {
      pNBPItem.setFontWeight(Iceutility.toBoolean(strFontWeight));
    }

    let strFontItalic = pitem.getStringValue(pitem.attributes, notebook_constants.szFONTITALIC_TOKEN);
    if (strFontItalic.length > 0) {
      pNBPItem.setFontStyle(Iceutility.toLong(strFontItalic));
    }

    let strPageLayout = pitem.getStringValue(pitem.attributes, notebook_constants.szPAGELAYOUT_TOKEN);
    if (strPageLayout.length > 0) {
      pNBPItem.setNotePageLayout(<IceTypes.PageLayouts>Iceutility.toLong(strPageLayout));
    }

    let strVScroll = pitem.getStringValue(pitem.attributes, notebook_constants.szVSCROLL_TOKEN);
    if (strVScroll.length > 0) {
      pNBPItem.setVScroll(Iceutility.toBoolean(strVScroll));
    }

    let strHScroll = pitem.getStringValue(pitem.attributes, notebook_constants.szHSCROLL_TOKEN);
    if (strHScroll.length > 0) {
      pNBPItem.setHScroll(Iceutility.toBoolean(strHScroll));
    }

    let strMultiPage = pitem.getStringValue(pitem.attributes, notebook_constants.szMULTIPG_TOKEN);
    if (strMultiPage.length> 0) {
      pNBPItem.setMultiPage(Iceutility.toBoolean(strMultiPage));
    }

    let strShowDate = pitem.getStringValue(pitem.attributes, notebook_constants.szSHOWDATE_TOKEN);
    if (strShowDate.length > 0) {
      pNBPItem.setShowDate(Iceutility.toBoolean(strShowDate));
    }

    let strShowTime = pitem.getStringValue(pitem.attributes, notebook_constants.szSHOWTIME_TOKEN);
    if (strShowTime.length > 0) {
      pNBPItem.setShowTime(Iceutility.toBoolean(strShowTime));
    }

    let strUseSummary = pitem.getStringValue(pitem.attributes, notebook_constants.szUSESUMMARY_TOKEN);
    if (strUseSummary.length > 0) {
      pNBPItem.setUseSummary(Iceutility.toBoolean(strUseSummary));
    }

    let strDefaultData = pitem.getStringValue(pitem.attributes, notebook_constants.szDEFAULTDATA_TOKEN);
    if (strDefaultData.length > 0) {
      pNBPItem.setDefaultData(strDefaultData);
    }

    let strNoteMode = pitem.getStringValue(pitem.attributes, notebook_constants.szNOTEMODE_TOKEN);
    if (strNoteMode.length > 0) {
      pNBPItem.setNoteMode(<IceTypes.DataMode>Iceutility.toLong(strNoteMode));
    }

    let strInPlaceEditing = pitem.getStringValue(pitem.attributes, notebook_constants.szINPLACEEDITING_TOKEN);
    if (strInPlaceEditing.length > 0) {
      pNBPItem.setInplaceEditing(Iceutility.toBoolean(strInPlaceEditing));
    }

    let strAlign = pitem.getStringValue(pitem.attributes, notebook_constants.szALIGN_TOKEN);
    if (strAlign.length > 0) {
      pNBPItem.setAlign(strAlign);
    }

    let strSpinStart = pitem.getStringValue(pitem.attributes, notebook_constants.szSPINSTART_TOKEN);
    if (strSpinStart.length > 0) {
      pNBPItem.setSpinStart(Iceutility.toDouble(strSpinStart));
    }

    let strSpinIncr = pitem.getStringValue(pitem.attributes, notebook_constants.szSPININCREMENT_TOKEN);
    if (strSpinIncr.length > 0) {
      pNBPItem.setSpinIncrement(Iceutility.toDouble(strSpinIncr));
    }

    let strBorderLeft = pitem.getStringValue(pitem.attributes, notebook_constants.szBORDERLEFT_TOKEN);
    if (strBorderLeft.length > 0) {
      let pBorders = pNBPItem.getBorders();
      pBorders.left = Iceutility.toLong(strBorderLeft);
    }

    let strBorderTop = pitem.getStringValue(pitem.attributes, notebook_constants.szBORDERTOP_TOKEN);
    if (strBorderTop.length > 0) {
      let pBorders = pNBPItem.getBorders();
      pBorders.top = Iceutility.toLong(strBorderTop);
    }

    let strBorderRight = pitem.getStringValue(pitem.attributes, notebook_constants.szBORDERRIGHT_TOKEN);
    if (strBorderRight.length > 0) {
      let pBorders = pNBPItem.getBorders();
      pBorders.right = Iceutility.toLong(strBorderRight);
    }

    let strBorderBottom = pitem.getStringValue(pitem.attributes, notebook_constants.szBORDERBOTTOM_TOKEN);
    if (strBorderBottom.length > 0) {
      let pBorders = pNBPItem.getBorders();
      pBorders.bottom = Iceutility.toLong(strBorderBottom);
    }

    let strBackColor = pitem.getStringValue(pitem.attributes, notebook_constants.szBACKCOLOR_TOKEN);
    if (strBackColor.length > 0) {
      pNBPItem.setBackColor(strBackColor);
    }

    return true;
  }

  fromXML(xml: string, pNBPage: NotebookPageObject): boolean {
    let page: notebookpage = this.formSvc.geticexdocumentobjects(xml, ["NOTEBOOK_PAGE"]);
    if (page == null) return false;

    return this.fromDBObject(page, pNBPage);
  }

  fromDBObject(pPage: notebookpage, pNBPage: NotebookPageObject) {
    if (pPage == null || pNBPage == null) return false;

    let strBackcolor = pPage.attributes[notebook_constants.szBACKCOLOR_TOKEN];
    if (strBackcolor != null) {
      pNBPage.setBackColor(strBackcolor);
    }

    for (let iItem = 0; iItem < pPage.ITEMS.length; iItem++) {
      let pitem = pPage.ITEMS[iItem];
      if (pitem != null) {
        let pNBPItem = pNBPage.addItem(null);
        pNBPItem.setLoading();

        let type = pitem.getStringValue(pitem.attributes, notebook_constants.szTYPE_TOKEN);
        if (type != null) {
          type = type.toUpperCase();
          if (type == notebook_constants.szLABEL_TOKEN)
            pNBPItem.setControlType(ctrl_type.ctypLabel);
          else if (type == notebook_constants.szEDIT_TOKEN)
            pNBPItem.setControlType(ctrl_type.ctypEdit);
          else if (type == notebook_constants.szDATE_TOKEN)
            pNBPItem.setControlType(ctrl_type.ctypDate);
          else if (type == notebook_constants.szSS_TOKEN) {
            pNBPItem.setControlType(ctrl_type.ctypSS);
            pNBPItem.setSSControlType(ss_ctrl_type.sstypSS);
          }
          else if (type == notebook_constants.szMEDHIST_TOKEN) {
            pNBPItem.setControlType(ctrl_type.ctypListCtrl);
          }
          else if (type == notebook_constants.szHANDWRITING_TOKEN) {
            pNBPItem.setControlType(ctrl_type.ctypHandWritingCtrl);
          }
        }

        if (NotebookObject.copy(pNBPItem, pitem)) {
          if (pNBPItem.getControlType() == ctrl_type.ctypSS)
          {
            for (let iss = 0; iss < pitem.SSITEMS.length; iss++) {
              let pss = pitem.SSITEMS[iss];
              if (pss != null) {
                let pSSItem = pNBPItem.addSubItem(null);
                pSSItem.setLoading();

                let sstype = pss.getStringValue(pss.attributes, notebook_constants.szTYPE_TOKEN);
                if (sstype.length > 0) {
                  if (sstype == notebook_constants.szLIST_TOKEN) {
                    pSSItem.setSSControlType(ss_ctrl_type.sstypList);
                  }
                  else if (sstype == notebook_constants.szLABEL_TOKEN) {
                    pSSItem.setSSControlType(ss_ctrl_type.sstypLabel);
                  }
                  else if (sstype == notebook_constants.szEDIT_TOKEN) {
                    pSSItem.setSSControlType(ss_ctrl_type.sstypEdit);
                  }
                  else if (sstype == notebook_constants.szDATE_TOKEN) {
                    pSSItem.setSSControlType(ss_ctrl_type.sstypDate);
                  }
                  else if (sstype == notebook_constants.szRANGE_TOKEN) { // To do use to determine colum span
                    pSSItem.setSSControlType(ss_ctrl_type.sstypRange);
                  }
                  else if (sstype == notebook_constants.szCHECKBOX_TOKEN) {
                    pSSItem.setSSControlType(ss_ctrl_type.sstypCheckBox);
                  }
                  else if (sstype == notebook_constants.szSPIN_TOKEN) {
                    pSSItem.setSSControlType(ss_ctrl_type.sstypSpin);
                  }
                  else if (sstype == notebook_constants.szHANDWRITING_TOKEN) {
                    pSSItem.setSSControlType(ss_ctrl_type.sstypHandWriting);
                  }
                  else if (sstype == notebook_constants.szNOTES_TOKEN) {
                    pSSItem.setSSControlType(ss_ctrl_type.sstypNotes);
                  }
                  else if (sstype == notebook_constants.szSUMMARY_TOKEN) {
                    pSSItem.setSSControlType(ss_ctrl_type.sstypSummary);
                  }
                  else if (sstype == notebook_constants.szSIGNATURE_TOKEN) {
                    pSSItem.setSSControlType(ss_ctrl_type.sstypSignature);
                  }
                  else if (sstype == notebook_constants.szCOLUMNSIZE_TOKEN) { // To do use to determine colum size
                    pSSItem.setSSControlType(ss_ctrl_type.sstypColumnSize);
                  }

                  NotebookObject.copy(pSSItem, pss);
                }
              }
            }
          }
        }
      }
      return true;
    }
    return false;
  }

  create(nPageIndex: number, pParentWnd: HTMLDivElement): NotebookPageObject {
    this.m_pCurrentParentWnd = pParentWnd;

    // add new nb page
    let pNBPage = this.getPage(nPageIndex);
    if (null == pNBPage)
      return null;

    if (false == this.fromXML(pNBPage.layout, pNBPage)) {
      this.removePage(pNBPage);
      return null;
    }

    return this.createByDBObject(pNBPage, pParentWnd);
  }

  createByDBObject(pNBPage: NotebookPageObject, pParentWnd: HTMLDivElement): NotebookPageObject {
    if (null == pNBPage)
      return null;

    if (pNBPage.getItemCount() == 0 && false == this.fromDBObject(pNBPage.page, pNBPage))
      return null;

    this.m_pCurrentParentWnd = pParentWnd;

    // Create controls
    for (let i = 0; i < pNBPage.getItemCount(); i++)  
    {
      let pItem = pNBPage.getItem(i);
      if (pItem != null) {
        switch (pItem.getControlType()) {
          case ctrl_type.ctypLabel:
            {
              //CStatic * pStaticWnd = pItem -> CreateStaticWindow(:: GetNextDynCtrl(), pParentWnd);
              //if (NULL == pStaticWnd) {
              //  return NULL;
              //}
              break;
            }
          case ctrl_type.ctypEdit:
            {
              //CBaseEdit * pEdtWnd = pItem -> CreateEditWindow(:: GetNextDynCtrl(), pParentWnd);
              //if (NULL == pEdtWnd) {
              //  return NULL;
              //}
              break;
            }
          case ctrl_type.ctypDate:
            {
              //CEditDate * pEdtWnd = pItem -> CreateEditDateWindow(:: GetNextDynCtrl(), pParentWnd);
              //if (NULL == pEdtWnd) {
              //  return NULL;
              //}
              break;
            }
          case ctrl_type.ctypSS:
            {
              if (pItem.getSSControlType() == ss_ctrl_type.sstypSS)
              {
              //  let pGrid = pItem.createSSWindow("NBIO" + pItem.n.notebook_page_key);
              //  if (null == pGrid)
              //    return null;

              //  pGrid.setNotebookPageIndex(pNBPage.getPageIndex(), this);
              }
              break;
            }
          case ctrl_type.ctypListCtrl:
            {
              //CWnd * pWnd = pItem -> CreateListCtrl(0, LVS_REPORT | WS_BORDER | WS_TABSTOP | WS_CHILD | WS_VISIBLE, pParentWnd);
              //if (NULL == pWnd) {
              //  return NULL;
              //}
              break;
            }
          case ctrl_type.ctypHandWritingCtrl:
            {
              //CBaseGrphttiCtrl * pHW = pItem -> CreateHandWritingCtrl(:: GetNextDynCtrl(), pParentWnd);
              //if (NULL == pHW) {
              //  return NULL;
              //}
              break;
            }
        }
      }
    }
    return pNBPage;
  }

  initUpdate(formCtrl: IcecontrolSettings, acttype: IceTypes.ACCOUNT_TYPE, act: string, nIndex: number): void
  {
    if (formCtrl == null)
      return;

    let pPageObj = this.getPage(nIndex);
    if (null == pPageObj)
      return;

    try {
      for (let i = 0; i < pPageObj.getItemCount(); i++ )
      {
        let pItem = pPageObj.getItem(i);
        if (pItem != null) {
          switch (pItem.getControlType()) {
            case ctrl_type.ctypLabel:
              {
                /*
                CRect rect = pItem->GetPosition();
                long nCol = rect.left;
                long nRow = rect.top;

                CStatic* pStaticWnd = (CStatic*)pItem->GetWnd();
                if ( NULL == pStaticWnd )
                  break;

                pStaticWnd->ShowWindow(SW_SHOW);
                */
                break;
              }
            case ctrl_type.ctypEdit:
              {
                /*
                CRect rect = pItem->GetPosition();
                long nCol = rect.left;
                long nRow = rect.top;
                CBaseEdit* pEdtWnd = (CBaseEdit*)pItem->GetWnd();
                if ( NULL == pEdtWnd )
                  break;

                pEdtWnd->ShowWindow(SW_SHOW);
                */
                break;
              }
            case ctrl_type.ctypListCtrl:
              {
                /*
                CRect rect = pItem->GetPosition();
                long nCol = rect.left;
                long nRow = rect.top;
                CBaseListCtrl* pListCtrl = (CBaseListCtrl*)pItem->GetWnd();
                if ( NULL == pListCtrl )
                  break;

                //pMedHistWnd->SetShowHeader(TRUE);
                //pMedHistWnd->SetPatientId(1L);	
                //pMedHistWnd->SetPatientName(pSheet->m_patObj.m_patient_contact.m_fname, pSheet->m_patObj.m_patient_contact.m_lname);
                //pMedHistWnd->SetVisitDate(pSheet->m_visObj.m_patient_visit.m_visit_date.Format(VAR_DATEVALUEONLY));
                //pMedHistWnd->SetAge(pSheet->m_visObj.m_patient_visit.m_age);
                //pMedHistWnd->SetWeight(pSheet->m_visObj.m_patient_visit.m_weight);
                //pMedHistWnd->SetTemperature(pSheet->m_visObj.m_patient_visit.m_temperature);
                //pMedHistWnd->SetBloodPressure(pSheet->m_visObj.m_patient_visit.m_blood_pressure);
                //pMedHistWnd->SetPregnant(pSheet->m_visObj.m_patient_visit.m_pregnant_option);

                pListCtrl->ShowWindow(SW_SHOW);
                */
                break;
              }
            case ctrl_type.ctypHandWritingCtrl:
              {
                /*
                CRect rect = pItem->GetPosition();
                CBaseGrphttiCtrl* pHW = (CBaseGrphttiCtrl*)pItem->GetWnd();
                if ( NULL == pHW )
                  break;

                pHW->ShowWindow(SW_SHOW);
                */
                break;
              }
            case ctrl_type.ctypSS:
              {
                let pGrid = pItem.getWnd();
                if (null == pGrid)
                  break;

                pGrid.setDefaultSourceType(this.getDefaultSourceType());

                for (let iSubItem = 0; iSubItem < pItem.getSubItemCount(); iSubItem++ ) {
                  let pItemTest = pItem.getSubItem(iSubItem);
                  if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypList) {
                    let pItemSSList = pItemTest;

                    let ctrl = pItemSSList.getAssociatedWindow();
                    if (ctrl != null) {
                      let strDefaultData = pItemSSList.getDefaultData();
                      if (Iceutility.isStringUndefinedNullOrEmpty(strDefaultData) == false) {
                        let slist = strDefaultData.split(';');
                        if (slist != null) {
                          let opts = [];
                          for (let ilist = 0; ilist < slist.length; ilist++) {
                            let option = slist[ilist];
                            let txt = "";
                            let val = "";
                            let parts = option.split('=');
                            if (parts.length > 0)
                              txt = parts[0];
                            if (parts.length > 1)
                              val = parts[1];

                            if (val == null || val.length == 0)
                              val = txt;

                            opts.push({ name: txt, value: val });
                          }

                          ctrl.setOptions(opts);
                        }
                      }

                      switch (pItemSSList.getFieldType()) {
                        case fld_type.ftypField: {
                          if (pItemSSList.getFieldName().toLowerCase() == "state_id") {
                            this.appSvc.setStateIDList(this.codeSvc, ctrl, "");
                          }
                          else if (pItemSSList.getFieldName().toLowerCase() == "country_id") {
                            this.appSvc.setCountryCodeList(this.codeSvc, ctrl);
                          }
                          break;
                        }
                        case fld_type.ftypCustom: {
                          /*
                          ice.customfields.customfield field;
                          if (sobj.cachegetcustomfield(sobj.getclientsession(), acttype, act, pItemSSList.GetTableName(), pItemSSList.GetFieldName(), out field)) {
                            string strListText;
                            // Values
                            ice.customfields.customoption option;
                            if (sobj.cachegetoptionvalues(sobj.getclientsession(), acttype, act, field.CUSTOM_OPTION_KEY, out option)) {
                              List < ice.customfields.customoptionvalue > vals = option.getvalues();
                              if (vals != null) {
                                List<ice.customfields.customoptionvalue>.Enumerator valiter = vals.GetEnumerator();
                                while (valiter.MoveNext()) {
                                  ice.customfields.customoptionvalue custoptval = valiter.Current;
                                  if (custoptval != null) {
                                    dl.Items.Add(new ListItem(custoptval.DISPLAY_VALUE, custoptval.FIELD_VALUE));
                                  }
                                }
                              }
                            }
                          }
                          */
                          }
                          break;
                        case fld_type.ftypOption:
                          break;
                      }
                    }
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypLabel) {
                    let pItemSSLabel = pItemTest;
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypEdit) {
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypDate) {
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypNotes) {
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypSignature) {
                    let pItemSSSignature = pItemTest;
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypRange) {
                    let pItemSSRange = pItemTest;
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypCheckBox) {
                    let pItemSSCheckBox = pItemTest;
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypSpin) {
                    let pItemSSSpin = pItemTest;
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypSummary) {
                    let pItemSSSummary = pItemTest;
                  }
                } // for loop thru ss items					

                break;
              }
          } // switch ctrl type
        } // item null check
      } // Loop items
    }
    catch (error) {
      console.log(error);
    }
  }

  getControlData(): boolean {
    let ret = false;

    try {
      for (let iPage = 0; iPage < this.getPageCount(); iPage++) {
        let pPageObj = this.getPage(iPage);
        if (pPageObj == null) continue;

        //////////////////////////////////////////////////////////////
        // take data from controls and place into Data record(s)
        for (let i = 0; i < pPageObj.getItemCount(); i++) {
          let pItem = pPageObj.getItem(i);
          if (pItem != null) {
            switch (pItem.getControlType()) {
              case ctrl_type.ctypLabel:
                {
                  let pStaticWnd = pItem.getWnd();
                  if (null == pStaticWnd)
                    break;

                  break;
                }
              case ctrl_type.ctypEdit:
                {
                  let pEdtWnd = pItem.getWnd();
                  if (null == pEdtWnd)
                    break;

                  switch (pItem.getFieldType()) {
                    case fld_type.ftypField: {
                        //if (pItem.isModified()) {
                        //}
                      }
                      break;
                    case fld_type.ftypCustom:
                      {
                        let txtCtrl: HTMLInputElement = <HTMLInputElement>pEdtWnd;
                        if (txtCtrl != null) {                          
                          this.setCustomFieldDataValue(pItem.getTableName(), pItem.getFieldName(), txtCtrl.value);
                        }
                      }

                      break;
                    case fld_type.ftypOption:
                      break;
                  }

                  break;
                }
              case ctrl_type.ctypHandWritingCtrl:
                {
                  let pHW: HTMLCanvasElement = <HTMLCanvasElement>pItem.getWnd();
                  // let pHW: iceMultimediaCanvas = <iceMultimediaCanvas>pItem.getWnd();
                  if (null == pHW)
                    break;

                  switch (pItem.getFieldType()) {
                    case fld_type.ftypField:
                      {
                      //  if (pItem.isModified()) {
                      //  }
                      }
                      break;
                    case fld_type.ftypCustom:
                      {
                        //if (false == pHW.getModifiedFlag())
                        //  break;

                        // now do the normal stuff
                        // put the delim for consistancy
                        //let strNewGraphettiData = pHW.getGraphettiNote();
                        //this.setCustomFieldDataValue(pItem.getTableName(), pItem.getFieldName(), strNewGraphettiData);
                      }

                      break;
                    case fld_type.ftypOption:
                      break;
                  }
                  break;
                }
              case ctrl_type.ctypListCtrl:
                {
                  let pWnd: HTMLSelectElement = <HTMLSelectElement>pItem.getWnd();
                  if (null == pWnd)
                    break;

                  break;
                }
              case ctrl_type.ctypSS:
                {
                  // use the assoc window because there are sub control on ss
                  let pGrid: NotebookTable = <NotebookTable>pItem.getWnd();
                  if (null == pGrid)
                    break;

                  for (let iSubItem = 0; iSubItem < pItem.getSubItemCount(); iSubItem++ )
                  {
                    let pItemTest = pItem.getSubItem(iSubItem);
                    if (pItemTest == null) continue;

                    let ctrl = pItemTest.getAssociatedWindow();
                    if (ctrl == null) continue;

                    if (pItemTest.getSSControlType() == ss_ctrl_type.sstypList) {
                      let pItemSSList = pItemTest;
                      switch (pItemSSList.getFieldType()) {
                        case fld_type.ftypField: {
                          let tbName = pItemSSList.getTableName();
                          let fldName = pItemSSList.getFieldName();
                          let srcData = this.m_data_record[tbName];
                          if (srcData != null) {
                            srcData[fldName] = ctrl.getFormControl().value;
                          }
                          break;
                        }
                        case fld_type.ftypCustom: {
                          let tbName = pItemSSList.getTableName();
                          let fldName = pItemSSList.getFieldName();
                          let valOfCtrl = ctrl.getFormControl().value;
                          if (valOfCtrl != null && valOfCtrl.toUpperCase() == basecontrol.szCLICKARROW || valOfCtrl.toUpperCase() == basecontrol.szCLICKHERE) {
                            valOfCtrl = "";
                          }

                          this.setCustomFieldDataValue(tbName, fldName, valOfCtrl);
                          break;
                        }
                        case fld_type.ftypOption:
                          break;
                      }
                    }
                    else if (pItemTest.getSSControlType() == ss_ctrl_type.sstypLabel) {
                      let pItemSSLabel = pItemTest;
                    }
                    else if (pItemTest.getSSControlType() == ss_ctrl_type.sstypEdit) {
                      let pItemSSEdit = pItemTest;

                      switch (pItemSSEdit.getFieldType()) {
                        case fld_type.ftypField: {
                          let tbName = pItemSSEdit.getTableName();
                          let fldName = pItemSSEdit.getFieldName();
                          let srcData = this.m_data_record[tbName];
                          if (srcData != null) {
                            srcData[fldName] = ctrl.getFormControl().value;
                          }
                          break;
                        }
                        case fld_type.ftypCustom: {
                          let tbName = pItemSSEdit.getTableName();
                          let fldName = pItemSSEdit.getFieldName();
                          let valOfCtrl = ctrl.getFormControl().value;
                          if (valOfCtrl != null && valOfCtrl.toUpperCase() == basecontrol.szCLICKARROW || valOfCtrl.toUpperCase() == basecontrol.szCLICKHERE) {
                            valOfCtrl = "";
                          }

                          this.setCustomFieldDataValue(tbName, fldName, valOfCtrl);
                          //  EXTERNALDOCUMENTHEADER_STORAGE extDocStorage;
                          //  if (pGrid -> GetNewExternalData(nCol, nRow, extDocStorage))
                          //    SetNewExternalDocuments(extDocStorage);
                          break;
                        }
                        case fld_type.ftypOption:
                          break;
                      }
                    }
                    else if (pItemTest.getSSControlType() == ss_ctrl_type.sstypHandWriting) {
                      let pItemSSHandWriting = pItemTest;

                      switch (pItemSSHandWriting.getFieldType()) {
                        case fld_type.ftypField:
                          break;
                        case fld_type.ftypCustom:
                          {
                            if (true/*pItemSSHandWriting.ismodified()*/) {
                            //  let strGraphettiData = pGrid.getGraphettiNote(nCol, nRow);
                            //  this.setCustomFieldDataValue(pItemSSHandWriting.getTableName(), pItemSSHandWriting.etFieldName(), strGraphettiData);
                            //  EXTERNALDOCUMENTHEADER_STORAGE extDocStorage;
                            //  if (pGrid -> GetNewExternalData(nCol, nRow, extDocStorage))
                            //    SetNewExternalDocuments(extDocStorage);
                            }
                          }
                          break;
                        case fld_type.ftypOption:
                          break;
                      }
                    }
                    else if (pItemTest.getSSControlType() == ss_ctrl_type.sstypNotes) {
                      let pItemSSNotes = pItemTest;

                      switch (pItemSSNotes.getFieldType()) {
                        case fld_type.ftypField:
                          break;
                        case fld_type.ftypCustom:
                          {
                            if (true/*pItemSSNotes.ismodified()*/) {
                            //  let strGraphettiData = pGrid.getGraphettiNote(nCol, nRow);
                            //  this.setCustomFieldDataValue(pItemSSNotes.getTableName(), pItemSSNotes.getFieldName(), strGraphettiData);
                            }
                          }
                          break;
                        case fld_type.ftypOption:
                          break;
                      }
                    }
                    else if (pItemTest.getSSControlType() == ss_ctrl_type.sstypSignature) {
                      let pItemSSSignature = pItemTest;
                      switch (pItemSSSignature.getFieldType()) {
                        case fld_type.ftypField:
                          break;
                        case fld_type.ftypCustom:
                          {
                            if (true/*pItemSSSignature.isModified()*/) {
                            //  let strGraphettiData = pGrid.getGraphettiNote(nCol, nRow);
                            //  this.setCustomFieldDataValue(pItemSSSignature.getTableName(), pItemSSSignature.getFieldName(), strGraphettiData);
                            }
                          }
                          break;
                        case fld_type.ftypOption:
                          break;
                      }
                    }
                    else if (pItemTest.getSSControlType() == ss_ctrl_type.sstypCheckBox) {
                      let pItemSSCheckBox = pItemTest;
                      switch (pItemSSCheckBox.getFieldType()) {
                        case fld_type.ftypField: {
                          let tbName = pItemSSCheckBox.getTableName();
                          let fldName = pItemSSCheckBox.getFieldName();
                          let srcData = this.m_data_record[tbName];
                          if (srcData != null) {
                            srcData[fldName] = ctrl.getFormControl().value;
                          }
                          break;
                        }
                        case fld_type.ftypCustom:
                          {
                            if (true/*pItemSSCheckBox.ismodified()*/) {
                              let cb = null; //pGrid.quickGetCellTypeCheckbox(nCol, nRow);
                              if (cb != null) {
                                let strText = cb.checked ? "1" : "0";
                                this.setCustomFieldDataValue(pItemSSCheckBox.getTableName(), pItemSSCheckBox.getFieldName(), strText);
                              }
                            }
                          }
                          break;
                        case fld_type.ftypOption:
                          break;
                      }
                    }
                    else if (pItemTest.getSSControlType() == ss_ctrl_type.sstypSpin) {
                      let pItemSSSpin = pItemTest;
                      switch (pItemSSSpin.getFieldType()) {
                        case fld_type.ftypField:
                          break;
                        case fld_type.ftypCustom:
                          {
                            if (true/*pItemSSSpin.isModified()*/) {
                            //  let strText = pGrid.quickGetText(nCol, nRow);
                            //  SetCustomFieldDataValue(pItemSSSpin -> GetTableName(), pItemSSSpin -> GetFieldName(), strText);
                            }
                          }
                          break;
                        case fld_type.ftypOption:
                          break;
                      }
                    }
                    else if (pItemTest.getSSControlType() == ss_ctrl_type.sstypDate) {
                      let pItemSSDate = pItemTest;
                      switch (pItemSSDate.getFieldType()) {
                        case fld_type.ftypField: {
                          let tbName = pItemSSDate.getTableName();
                          let fldName = pItemSSDate.getFieldName();
                          let srcData = this.m_data_record[tbName];
                          if (srcData != null) {
                            srcData[fldName] = Iceutility.GetDateText(ctrl.getFormControl().value);
                          }
                          break;
                        }
                        case fld_type.ftypCustom:
                          {
                            if (true/*pItemSSDate.ismodified()*/) {
                              let txt = null; //pGrid.quickGetCellTypeInput(nCol, nRow);
                              if (txt != null) {
                                let strText = txt.value;
                                if (strText.toUpperCase() == basecontrol.szCLICKARROW || strText.toUpperCase() == basecontrol.szCLICKHERE) {
                                  strText = "";
                                }

                                this.setCustomFieldDataValue(pItemSSDate.getTableName(), pItemSSDate.getFieldName(), strText);
                              }
                            }
                          }
                          break;
                        case fld_type.ftypOption:
                          break;
                      }
                    }
                    else if (pItemTest.getSSControlType() == ss_ctrl_type.sstypSummary) {
                      let pItemSSSummary = pItemTest;
                      switch (pItemSSSummary.getFieldType()) {
                        case fld_type.ftypField:
                          break;
                        case fld_type.ftypCustom:
                          {
                            if (true/*pItemSSSummary.isModified()*/) {
                            //  CString strText = _T("");
                            //  for (int nSumRow = nRow; nSumRow < pItemSSSummary -> GetNextSummaryRow(); nSumRow++)
                            //  {
                            //    strText += pGrid -> QuickGetText(nCol, nSumRow);
                            //    strText += _T("|");
                            //  }
                            //  SetCustomFieldDataValue(pItemSSSummary -> GetTableName(), pItemSSSummary -> GetFieldName(), strText);
                            }
                          }
                          break;
                        case fld_type.ftypOption:
                          break;
                      }
                    }
                  }
                  break;
                }
            } // switch ctrl type
          } // item null check
        } // Loop items
      }
      ret = true;
    }
    catch(error){
      console.log(error);
      return null;
    }
    return ret;
  }

  setControlData(formCtrl: IcecontrolSettings, acttype: IceTypes.ACCOUNT_TYPE, act: string, nIndex: number): void {
    for (let ipage = 0; ipage < this.getPageCount(); ipage++ )
    {
      let pPageObj = this.getPage(ipage);
      if (null == pPageObj)
        continue;

      if (nIndex >= 0 && nIndex != ipage) // only do this page
        continue;

      //////////////////////////////////////////////////////////////
      // Loop thru items and while retrieving data from Data Record(s)
      for (let i = 0; i < pPageObj.getItemCount(); i++ )
      {
        let pItem = pPageObj.getItem(i);
        if (pItem != null) {
          //pItem.setmodified(false);

          switch (pItem.getControlType()) {
            case ctrl_type.ctypLabel:
              {
                /*
                CStatic* pStaticWnd = (CStatic*)pItem->GetWnd();
                if ( NULL == pStaticWnd )
                  break;
                */
                break;
              }
            case ctrl_type.ctypEdit:
              {
                let pEdtWnd: HTMLInputElement = <HTMLInputElement>pItem.getWnd();
                if ( null == pEdtWnd )
                  break;

                switch(pItem.getFieldType())
                {
                case fld_type.ftypField:
                  {
                  }
                  break;
                case fld_type.ftypCustom:
                  {
                    let strText = this.getCustomFieldDataValue(pItem.getTableName(), pItem.getFieldName());
                    pEdtWnd.textContent = strText;
                  }
                  break;
                case fld_type.ftypOption:
                  break;
                }
                break;
              }
            case ctrl_type.ctypHandWritingCtrl:
              {
                let pHW: HTMLCanvasElement = <HTMLCanvasElement>pItem.getWnd();
                if ( null == pHW )
                  break;

                switch(pItem.getFieldType())
                {
                case fld_type.ftypField:
                  {
                  }
                  break;
                case fld_type.ftypCustom:
                  {
                    let strGraphettiData = this.getCustomFieldDataValue(pItem.getTableName(), pItem.getFieldName());
                    //pHW->setGraphettiNote(strGraphettiData);
                  }
                  break;
                  case fld_type.ftypOption:
                  break;
                }
                break;
              }
            case ctrl_type.ctypDate:
              {                
                let pEdtWnd = pItem.getWnd();
                if ( null == pEdtWnd )
                  break;

                switch(pItem.getFieldType())
                {
                case fld_type.ftypField:
                  {
                  }
                  break;
                case fld_type.ftypCustom:
                  break;
                case fld_type.ftypOption:
                  break;
                }
                
                break;
              }
            case ctrl_type.ctypListCtrl:
              {
                /*
                CWnd* pWnd = pItem->GetWnd();
                if ( NULL == pWnd 
                  break;
                */
                break;
              }
            case ctrl_type.ctypSS:
              {
                let pGrid: NotebookTable = <NotebookTable>pItem.getWnd();
                if (null == pGrid)
                  break;

                for (let iSubItem = 0; iSubItem < pItem.getSubItemCount(); iSubItem++)
                {
                  let pItemTest = pItem.getSubItem(iSubItem);
                  let ctrl = pItemTest.getAssociatedWindow();
                  if (pItemTest == null || ctrl == null) continue;

                  if (pItemTest.getSSControlType() == ss_ctrl_type.sstypList) {
                    let pItemSSList = pItemTest;

                    switch (pItemSSList.getFieldType()) {
                      case fld_type.ftypField:
                        {
                          let tbName = pItemSSList.getTableName();
                          let fldName = pItemSSList.getFieldName();
                          let srcData = this.m_data_record[tbName];
                          if (srcData != null) {
                            let srcVal = srcData[fldName];
                            ctrl.getFormControl().setValue(srcVal);
                          }
                        }
                        break;
                      case fld_type.ftypCustom:
                        {
                          let strText = this.getCustomFieldDataValue(pItemSSList.getTableName(), pItemSSList.getFieldName());
                          let list = null; //pGrid.quickGetCellTypeSelect(nCol, nRow);
                          if (null == list)
                            break;

                          if (Iceutility.isStringUndefinedNullOrEmpty(strText)) {
                            let opt = new HTMLOptionElement();
                            opt.text = basecontrol.szCLICKARROW;
                            opt.value = basecontrol.szCLICKARROW;
                            opt.selected = true;
                            list.add(opt);
                          }
                        }
                        break;
                      case fld_type.ftypOption:
                        break;
                    }
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypLabel) {
                    let pItemSSLabel = pItemTest;

                    let strCaption = pItemSSLabel.getCaption();
                    if (Iceutility.isStringUndefinedNullOrEmpty(strCaption)) {
                      switch (pItemSSLabel.getFieldType()) {
                        case fld_type.ftypField:
                          {
                          }
                          break;
                        case fld_type.ftypCustom:
                          {
                            let strText = this.getCustomFieldDataValue(pItemSSLabel.getTableName(), pItemSSLabel.getFieldName());
                            let lbl = null; //pGrid.quickGetCellTypeLabel(nCol, nRow);
                            if (null == lbl)
                              break;

                            lbl.textContent = strText;
                          }
                          break;
                        case fld_type.ftypOption:
                          break;
                      }
                    }
                    else {
                      let strText = this.getCustomFieldDataValue(pItemSSLabel.getTableName(), pItemSSLabel.getFieldName());
                      let lbl = null; //pGrid.quickGetCellTypeLabel(nCol, nRow);
                      if (null == lbl)
                        break;

                      lbl.textContent = strText;
                    }
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypEdit) {
                    let pItemSSEdit = pItemTest;
                    switch (pItemSSEdit.getFieldType()) {
                      case fld_type.ftypField:
                        {
                          let tbName = pItemSSEdit.getTableName();
                          let fldName = pItemSSEdit.getFieldName();
                          let srcData = this.m_data_record[tbName];
                          if (srcData != null) {
                            let srcVal = srcData[fldName];
                            ctrl.getFormControl().setValue(srcVal);
                          }
                        }
                        break;
                      case fld_type.ftypCustom:
                        {
                          let strText = this.getCustomFieldDataValue(pItemSSEdit.getTableName(), pItemSSEdit.getFieldName());
                          if (Iceutility.isStringUndefinedNullOrEmpty(strText))
                            strText = pItemSSEdit.getCaption();

                          let txtBox = null; //pGrid.quickGetCellTypeInput(nCol, nRow);
                          if (null == txtBox)
                            break;

                          txtBox.value = Iceutility.isStringUndefinedNullOrEmpty(strText) ? basecontrol.szCLICKHERE : strText;

                          //pGrid.SetGraphettiNote(nCol, nRow, strGraphettiData);
                          //if ( strText.IsEmpty() && strGraphettiData.IsEmpty() == FALSE ) // grph preset
                          //    pGrid.SetHandwriting(nCol, nRow, strGraphettiData, pItemSSEdit->GetNotePageLayout());
                        }
                        break;
                      case fld_type.ftypOption:
                        break;
                    }                   
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypHandWriting) {
                    /*
                    CNoteBookPageItem* pItemSSHandWriting = pItemTest;
                    CRect rect = pItemSSHandWriting->GetSSPosition();
                    nCol = rect.left - 1;
                    nRow = rect.top - 1;

                    switch(pItemSSHandWriting->GetFieldType())
                    {
                    case CNoteBookPageItem::ftypField:
                      break;
                    case CNoteBookPageItem::ftypCustom:
                      {
                        CString strText, strGraphettiData;
                        CString strStd = GetCustomFieldDataValue(pItemSSHandWriting->GetTableName(), pItemSSHandWriting->GetFieldName());
                        CGrphttiManager::ParseGraphetiiFormat(strStd, strText, strGraphettiData);

                        pGrid->SetGraphettiNote(nCol, nRow, strGraphettiData, TRUE);
                        pGrid->SetHandwriting(nCol, nRow, strGraphettiData, pItemSSHandWriting->GetNotePageLayout());
                      }
                      break;
                    case CNoteBookPageItem::ftypOption:
                      break;
                    }
                    */
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypNotes) {
                    //////////////////////////////////////////////////////////////
                    //	0,0	Date	(Date Ctrl)				Name of Note (Label Ctrl)		SubType (Dropdownlist)
                    //	0,1	Note Status (Dropdownlist)		Provider Name (Dropdownlist)	Reviewed by Me (Link)
                    //	0,2	Graphetti Notes
                    //	...	...
                    //	0,?	Plain Notes
                    //	...	...
                    /*
                    notebookpageitem pItemSSNotes = pItemTest;
                    System.Drawing.Rectangle rect = pItemSSNotes.GetSSPosition();
                    nCol = rect.Left - 1;
                    nRow = rect.Top - 1;

                    switch(pItemSSNotes.GetFieldType())
                    {
                    case notebookpageitem.fld_type.ftypField:
                      {
                        string strText, strGraphettiData;

                        string strKeyField, strDateField, strNameField, strNoteTypeField, strStatusField, strPACTField;
                        CStringArray arExtractionFields, arOtherFields;
                        DATAOBJECTLIST* pList;
                        if (OnGetNotes(strKeyField, strDateField, strNameField, strNoteTypeField, strStatusField, strPACTField, arExtractionFields, arOtherFields, &pList))
                        {
                          //CString strStd = GetCustomFieldDataValue(pItemSSNotes->GetTableName(), pItemSSNotes->GetFieldName());
                          //CGrphttiManager::ParseGraphetiiFormat(strStd, strText, strGraphettiData);
                        }
                        pGrid->SetGraphettiNote(nCol, nRow, strGraphettiData, TRUE);
                        pGrid->SetHandwriting(nCol, nRow, strGraphettiData, pItemSSNotes->GetNotePageLayout());
    	
                        break;
                      }
                    case CNoteBookPageItem::ftypCustom:
                      {
                        CString strText, strGraphettiData;
                        CString strStd = GetCustomFieldDataValue(pItemSSNotes->GetTableName(), pItemSSNotes->GetFieldName());
                        CGrphttiManager::ParseGraphetiiFormat(strStd, strText, strGraphettiData);

                        pGrid->SetGraphettiNote(nCol, nRow, strGraphettiData, TRUE);
                        pGrid->SetHandwriting(nCol, nRow, strGraphettiData, pItemSSNotes->GetNotePageLayout());
                        break;
                      }
                    case CNoteBookPageItem::ftypOption:
                      break;
                    }
                    */
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypCheckBox) {
                    let pItemSSCheckBox = pItemTest;
                    switch (pItemSSCheckBox.getFieldType()) {
                      case fld_type.ftypField:
                        break;
                      case fld_type.ftypCustom:
                        {
                          let strText = this.getCustomFieldDataValue(pItemSSCheckBox.getTableName(), pItemSSCheckBox.getFieldName());
                          let chb = null; //pGrid.quickGetCellTypeCheckbox(nCol, nRow);
                          if (null == chb)
                            break;

                          chb.checked = strText == "1" ? true : false;
                          chb.textContent = pItemSSCheckBox.getCaption();
                        }
                        break;
                      case fld_type.ftypOption:
                        break;
                    }
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypSpin) {
                    /*
                    CNoteBookPageItem* pItemSSSpin = pItemTest;
                    CRect rect = pItemSSSpin->GetSSPosition();
                    nCol = rect.left - 1;
                    nRow = rect.top - 1;

                    switch(pItemSSSpin->GetFieldType())
                    {
                    case CNoteBookPageItem::ftypField:
                      break;
                    case CNoteBookPageItem::ftypCustom:
                      {
                        CString strText = GetCustomFieldDataValue(pItemSSSpin->GetTableName(), pItemSSSpin->GetFieldName());
                        pGrid->QuickSetText(nCol, nRow, strText);
                      }
                      break;
                    case CNoteBookPageItem::ftypOption:
                      break;
                    }
                    */
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypDate) {
                    let pItemSSDate = pItemTest;
                    let rect = pItemSSDate.getSSPosition();
                    switch (pItemSSDate.getFieldType()) {
                      case fld_type.ftypField:
                        let tbName = pItemSSDate.getTableName();
                        let fldName = pItemSSDate.getFieldName();
                        let srcData = this.m_data_record[tbName];
                        if (srcData != null) {
                          let srcVal = srcData[fldName];
                          ctrl.getFormControl().setValue(Iceutility.GetLocalDate(srcVal));
                        }

                        break;
                      case fld_type.ftypCustom:
                        {
                          let strText = this.getCustomFieldDataValue(pItemSSDate.getTableName(), pItemSSDate.getFieldName());
                          let txtBox = null; //pGrid.quickGetCellTypeInput(nCol, nRow);
                          if (null == txtBox)
                            break;

                          txtBox.textContent = Iceutility.isStringUndefinedNullOrEmpty(strText) ? basecontrol.szCLICKHERE : strText;
                        }
                        break;
                      case fld_type.ftypOption:
                        break;
                    }
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypSummary) {
                    /*
                    CNoteBookPageItem* pItemSSSummary = pItemTest;
                    CRect rect = pItemSSSummary->GetSSPosition();
                    nCol = rect.left - 1;
                    nRow = rect.top - 1;
                    long nEndRow = rect.bottom - 1;

                    switch(pItemSSSummary->GetFieldType())
                    {
                    case CNoteBookPageItem::ftypField:
                      break;
                    case CNoteBookPageItem::ftypCustom:
                      {
                        // Clear all summary rows
                        pItemSSSummary->SetNextSummaryRow(nRow);							
                        for ( int item_row = nRow; item_row < nEndRow + 1; item_row++ )
                        {
                          pGrid->QuickSetText(nCol, item_row, _T(""));
                        }

                        CString strText = GetCustomFieldDataValue(pItemSSSummary->GetTableName(), pItemSSSummary->GetFieldName());

                        CStringArray elems;
                        CColumnData::ParseString(strText, _T("|"), elems);

                        for ( int item = 0; item < elems.GetSize(); item++ )
                        {
                          pGrid->InsertNextSummaryItem(pItemSSSummary, elems.GetAt(item), NULL);
                        }
                      }
                      break;
                    case CNoteBookPageItem::ftypOption:
                      break;
                    }
                    */
                  }
                  else if (pItemTest != null && pItemTest.getSSControlType() == ss_ctrl_type.sstypSignature) {
                    /*
                    CNoteBookPageItem* pItemSSSignature = pItemTest;
                    CRect rect = pItemSSSignature->GetSSPosition();
                    nCol = rect.left - 1;
                    nRow = rect.top - 1;

                    switch(pItemSSSignature->GetFieldType())
                    {
                    case CNoteBookPageItem::ftypField:
                      break;
                    case CNoteBookPageItem::ftypCustom:
                      {
                        CString strGraphettiData = GetCustomFieldDataValue(pItemSSSignature->GetTableName(), pItemSSSignature->GetFieldName());
                        pGrid->QuickSetText(nCol, nRow, strGraphettiData.IsEmpty() ? szCLICKHERE : _T(""));
                        pGrid->SetGraphettiNote(nCol, nRow, strGraphettiData, TRUE);
                        pGrid->SetHandwriting(nCol, nRow, strGraphettiData, pItemSSSignature->GetNotePageLayout());
                      }
                      break;
                    case CNoteBookPageItem::ftypOption:
                      break;
                    }
                    */
                  }
                }
                break;
              }
          } // switch ctrl type
        } // item null check
      } // Loop items
    }
  }

  getCustomFieldData(): string {
    //ice.customfields.customfielddata flddata = getfielddata();
    //if (flddata != null && dataobject.isempty(m_strCField)) {
    //  flddata.toxml(out m_strCField);
    //}
    return "";
  }

  setCustomFieldData(lpszCustomFieldData: string): void {
  //  m_strCField = lpszCustomFieldData;
  //  ice.customfields.customfielddata flddata = getfielddata();
  //  if (flddata != null) {
  //    flddata.initialize();

  //    if (false == flddata.fromxml(ref m_strCField)) {
  //      //to do
  //    }
  //  }
  }

  setCustomFieldDataValue(lpszTable: string, lpszColumn: string, lpszData: string): void {
  //  ice.customfields.customfielddata flddata = getfielddata();
  //  if (flddata != null) {
  //    ice.customdatabase.database db = flddata.getdatarecord();
  //    if (db != null) {
  //      db.setdata(lpszTable, 0, lpszColumn, lpszData);
  //      setmodified(true);
  //    }
  //  }
  } 

  getCustomFieldDataValue(lpszTable: string,  lpszColumn: string): any {
    //ice.customfields.customfielddata flddata = getfielddata();
    //if (flddata != null) {
    //  ice.customdatabase.database db = flddata.getdatarecord();
    //  if (db != null) {
    //    string sdata;
    //    if (db.getdata(lpszTable, 0, lpszColumn, out sdata)) {
    //      return sdata;
    //    }
    //  }
    //}
    return {};
  }
            
  resetPages(): void {
    if (this.m_pages == null)
      return;

    this.m_pages = [];
  }

  addPage(incomingPageObj: NotebookPageObject, makeCurrent: boolean): NotebookPageObject {
    try {
      if (incomingPageObj == null)
        incomingPageObj = new NotebookPageObject(null);

      this.m_pages.push(incomingPageObj);
      let idx = this.m_pages.indexOf(incomingPageObj);
      incomingPageObj.setPageIndex(idx);
      if (this.currentPage == -1 || makeCurrent) {
        this.currentPage = idx;
      }
    }
    catch (error)
    {
      console.log(error);
    }
    return incomingPageObj;
  }

  getPage(nindex: number): NotebookPageObject {
    if (this.m_pages == null || this.m_pages.length == 0)
      return null;

    if (nindex >= 0 && nindex < this.m_pages.length) {
      return this.m_pages[nindex];
    }
    return null;
  }

  getPageByPageKey(lpszNBPageKey: string): NotebookPageObject {
    for (let i = 0; i < this.getPageCount(); i++ )
    {
      let pObj = this.getPage(i);
      if (pObj != null && pObj.page.notebook_page_key == lpszNBPageKey) {
        return pObj;
      }
    }
    return null;
  }

  getPageByPageName(lpszNBPageName: string): NotebookPageObject {
    for (let i = 0; i < this.getPageCount(); i++ )
    {
      let pObj = this.getPage(i);
      if (pObj != null && pObj.page.nbpage_name == lpszNBPageName) {
        return pObj;
      }
    }
    return null;
  }

  removePage(pPageObj: NotebookPageObject) {
    if (this.m_pages == null || this.m_pages.length == 0)
      return;

    delete this.m_pages[pPageObj.getPageIndex()];
    if (this.currentPage > 0 && this.currentPage <= pPageObj.getPageIndex()) {
      this.currentPage--; // reduce current page
    }
    // To do reset page indexes
  }

  getPageCount(): number {
    if (this.m_pages == null || this.m_pages.length == 0)
      return 0;

    return this.m_pages.length;
  }

  /*
  public bool RenderPage(notebookpageobject nbpobj, out string results)
  {
    results = null;

    if (null == nbpobj)
      return false;

    try {
      // show/hide controls
      for (int i = 0; i < nbpobj.GetItemCount(); i++)
      {
        notebookpageitem pItem = nbpobj.GetItem(i);
        if (pItem != null) {
          switch (pItem.GetControlType()) {
            case notebookpageitem.ctrl_type.ctypLabel:
              {
                break;
              }
            case notebookpageitem.ctrl_type.ctypEdit:
              {
                break;
              }
            case notebookpageitem.ctrl_type.ctypHandWritingCtrl:
              {
                break;
              }
            case notebookpageitem.ctrl_type.ctypDate:
              {
                break;
              }
            case notebookpageitem.ctrl_type.ctypSS:
              {
                if (pItem.GetSSControlType() == notebookpageitem.ss_ctrl_type.sstypSS) {
                  ice.controls.notebookgrid pGrid = (ice.controls.notebookgrid)pItem.GetWnd();
                  if (pGrid != null) {
                    System.IO.StringWriter sw = new System.IO.StringWriter();
                    System.Web.UI.HtmlTextWriter localwrter = new System.Web.UI.HtmlTextWriter(sw);
                    pGrid.RenderControl(localwrter);
                    if (dataobject.isempty(results))
                      results = sw.ToString();
                    else
                      results += sw.ToString();
                  }
                }

                break;
              }
            case notebookpageitem.ctrl_type.ctypListCtrl:
              {
                break;
              }
          }
        }
      }
      return dataobject.isempty(results) == false;
    }
    catch (Exception ex)
    {
      ice.dotnet.req.debug(ex.Message);
    }
    return false;
  }
  */

  getFieldData(): any {
    return this.m_data_record;
  }
}
